import {combineReducers} from 'redux';
import {activeLayersReducer} from './activeLayersReducer';
import {drawModeReducer} from './drawModeReducer';
import {setDynamicUrlMapProperties} from '../config/dynamicUrlHelper';
import {trans} from '../config/translation';

/**
 * Reducer for loading layers
 *
 * @param layers
 * @param action
 * @returns {*[]|*}
 */
const layersLoadReducer = (layers = [], action) => {
    if (action.type === 'LAYERS_LOADED') {
        return action.payload;
    }
    return layers;
};

/**
 * Reducer for activate/deactivate toggle for layer info popup modal
 *
 * @param layerInfoPopupData
 * @param action
 * @returns {{data: {}, status: boolean}|*}
 */
const layerInfoPopupDataReducer = (layerInfoPopupData = {}, action) => {
    if (action.type === 'DISPLAY_LAYER_INFO_POPUP') {
        return action.payload;
    }
    return {
        status: false,
        data: {}
    };
};

/**
 * Reducer for activate/deactivate toggle for restore layer popup modal
 *
 * @param restoreLayerPopupData
 * @param action
 * @returns {{data: {}, status: boolean}|*}
 */
const restoreLayerPopupDataReducer = (restoreLayerPopupData = {status: false}, action) => {
    if (action.type === 'DISPLAY_RESTORE_LAYER_POPUP') {
        return action.payload;
    }
    return restoreLayerPopupData;
};

/**
 * Reducer for activate/deactivate toggle for restore layer popup modal
 *
 * @param restoreLayerPopupData
 * @param action
 * @returns {{data: {}, status: boolean}|*}
 */
const geoserverAlertPopupDataReducer = (geoserverAlertPopupData = {status:false}, action) => {
    if (action.type === 'DISPLAY_GEOSERVER_ALERT_POPUP') {
        return action.payload;
    }
    return geoserverAlertPopupData;
};

/**
 * Reducer for activate/deactivate toggle for restore onboarding popup modal
 *
 * @param onboardingPopupData
 * @param action
 * @returns {{data: {}, status: boolean}|*}
 */
const onboardingPopupDataReducer = (onboardingPopupData = {status: false, items: []}, action) => {
    if (action.type === 'DISPLAY_ONBOARDING_POPUP') {
        return Object.assign({}, onboardingPopupData, {status: action.payload})
    }
    if(action.type === 'LOAD_ONBOARDING_POPUP_DATA') {
        return Object.assign({}, onboardingPopupData, {items: action.payload})
    }
    return onboardingPopupData;
};

/**
 * Reducer for map properties
 *
 * @param mapProperties
 * @param action
 * @returns {{}|*}
 */
const mapPropertiesReducer = (mapProperties = {}, action) => {
    if (action.type === 'UPDATE_MAP_PROPERTIES') {
        setDynamicUrlMapProperties(action.payload);
        return action.payload;
    }
    setDynamicUrlMapProperties(mapProperties);
    return mapProperties;
};

/**
 * Reducer for zoom to extent function
 *
 * @param extent
 * @param action
 * @returns {*[]|*}
 */
const mapExtentReducer = (extent = [], action) => {
    if (action.type === 'ZOOM_MAP_TO_EXTENT') {
        return action.payload;
    }
    return extent;
};

/**
 * Reducer for base layer chooser/toggler
 *
 * @param baseLayer
 * @param action
 * @returns {string|*}
 */
const baseLayerReducer = (baseLayer = '', action) => {
    if (action.type === 'UPDATE_BASE_LAYER') {
        return action.payload;
    }
    return baseLayer;
};

/**
 * Reducer for minifying legends toggle
 *
 * @param status
 * @param action
 * @returns {boolean|*}
 */
const legendsMinifiedReducer = (status = false, action) => {
    if (action.type === 'MINIFY_LEGENDS') {
        return action.payload;
    }
    return status;
};

/**
 * Reducer for detail panel show/hide
 *
 * @param data
 * @param action
 * @returns {boolean|*}
 */
const detailPanelDataReducer = (data = {status: false, type: null, data: null}, action) => {
    const rightPanelCentralizedControlData = rightPanelCentralizedControl('detail-panel', action);
    if(rightPanelCentralizedControlData !== null){
        return rightPanelCentralizedControlData;
    }

    if (action.type === 'DISPLAY_DETAIL_PANEL') {
        return action.payload;
    }
    return data;
};

/**
 * reducer for map tooltip data
 * @param data
 * @param action
 * @returns {{data: null, false: *, status: string}|*}
 */
const mapTooltipDataReducer = (data = {status: false, data: null}, action) => {
    if (action.type === 'DISPLAY_MAP_TOOLTIP') {
        return action.payload;
    }
    return data;
};

/**
 * reducer for display opacity slider
 *
 * @param data
 * @param action
 * @returns {boolean|*}
 */
const displayOpacitySliderReducer = (data = {status: false, data: null}, action) => {
    if(action.type === 'DISPLAY_OPACITY_SLIDER') {
        return action.payload;
    }
    if(action.type === 'LAYER_OPACITY_CHANGED'){
        return {
            ...data,
            layer: action.payload,
        }
    }
    return data;
};

/**
 * Siapalaga chart data reducer
 *
 * @param data
 * @param action
 * @returns {null|*}
 */
const sipalagaChartDataReducer = (data = null, action) => {
    if (action.type === 'UPDATE_SIPALAGA_DETAIL_PANEL_CHART') {
        return action.payload;
    }
    return data;
}

/**
 * chart zoom popup data reducer
 *
 * @param data
 * @param action
 * @returns {{status: boolean}|*}
 */
const chartZoomPopupDataReducer = (data = {status: false, data: {}}, action) => {
    if (action.type === 'DISPLAY_CHART_ZOOM_POPUP') {
        return action.payload;
    }
    return data;
}

/**
 * text zoom popup data reducer
 *
 * @param data
 * @param action
 * @returns {{status: boolean}|*}
 */
const chartTextPopupDataReducer = (data = {status: false, data: {}}, action) => {
    if (action.type === 'DISPLAY_TEXT_ZOOM_POPUP') {
        return action.payload;
    }
    return data;
}

/**
 * mitra restorasi filter data reducer
 *
 * @param data
 * @param action
 * @returns {{pendana_selected: [], pelaksana: [], pendana: [], pelaksana_selected: [], status: []}|*}
 */
const mitraRestorasiFilterDataReducer = (data = {
    pelaksana         : [],
    pendana           : [],
    pelaksana_selected: [],
    pendana_selected  : [],
    status            : []
}, action) => {
    if (action.type === 'UPDATE_MITRA_RESTORASI_FILTER_DATA') {
        return action.payload;
    }
    return data;
}

/**
 * hotspot additional filter data reducer
 *
 * @param data
 * @param action
 * @returns {{confidenceLevel: {label: string, value: number}, source: {label: string, value: number}}|*}
 */
const hotspotFilterDataReducer = (data = {
    confidenceLevel: {
        value: 30,
        label: trans('hotspot.confidence-level-30'),
    },
    source         : {
        value: 'ALL',
        label: 'ALL',
    },
}, action) => {
    if (action.type === 'HOTSPOT_FILTER_APPLIED') {
        return action.payload;
    }
    return data;
}

/**
 * multistack data reducer
 *
 * @param data
 * @param action
 * @returns {{data: [], status: boolean}|*}
 */
const multistackDataReducer = (data = {status: false, data: []}, action) => {
    if (action.type === 'DISPLAY_MULTISTACK') {
        return action.payload;
    }
    return data;
}

const mobileMenuReducer = (mobileMenu = false, action) => {
    if (action.type === 'TOGGLE_MOBILE_MENU') {
        if (mobileMenu === action.payload.item) {
            return false;
        } else if (action.payload.status === false) {
            return false;
        } else {
            return action.payload.item;
        }
    }
    return mobileMenu;
}

/**
 * adhoc layer panel status reducer
 *
 * @param data
 * @param action
 */
const adhocLayerPanelReducer = (data = {status: false}, action) => {
    const rightPanelCentralizedControlData = rightPanelCentralizedControl('adhoc-layer-panel', action);
    if(rightPanelCentralizedControlData !== null){
        return rightPanelCentralizedControlData;
    }

    if (action.type === 'TOGGLE_ADHOC_LAYER_PANEL') {
        if (action.payload) {
            return action.payload;
        }

        if (data.status === false) {
            return {
                status: true
            }
        } else {
            return {
                status: false
            }
        }

    }
    return data;
}

/**
 * centralize right panel hide/show helper
 *
 * @param type
 * @param action
 * @returns {{data: null, type: null, status: boolean}|null|{status: boolean}|{data: {}, report: boolean, type: boolean, status: boolean}}
 */
export const rightPanelCentralizedControl = (type, action) => {
    /* related action type
        DISPLAY_DETAIL_PANEL
        DISPLAY_DRAW_MODE_START_PANEL
        DRAW_MODE_DISPLAY_TOGGLE
        TOGGLE_ADHOC_LAYER_PANEL
    * */
    if (type === 'adhoc-layer-panel') {
        if (action.type === 'DISPLAY_DETAIL_PANEL'
            || action.type === 'DISPLAY_DRAW_MODE_START_PANEL'
            || action.type === 'DRAW_MODE_DISPLAY_TOGGLE'
        ) {
            if(action.payload && action.payload.status !== false) {
                return {status: false}
            }
        }
    }
    if (type === 'draw-mode-panel') {
        if (action.type === 'DISPLAY_DETAIL_PANEL') {
            return {
                status: false,
                type  : false,
                report: false,
                data  : {},
            }
        }
        if(action.type === 'TOGGLE_ADHOC_LAYER_PANEL'){
            return {
                status: false,
                type  : false,
                report: false,
                data  : {},
            }
        }
    }
    if (type === 'detail-panel') {
        if (action.type === 'TOGGLE_ADHOC_LAYER_PANEL'
            || action.type === 'DISPLAY_DRAW_MODE_START_PANEL'
            || action.type === 'DRAW_MODE_DISPLAY_TOGGLE'
        ) {
            return {
                status: false,
                type  : null,
                data  : null
            }
        }
    }
    return null;
}

/**
 * utility popup data reducer
 *
 * @param data
 * @param action
 * @returns {{type: null, status: boolean}|*}
 */
export const utilityPopupReducer = (data = {status: false, type: null}, action) => {
    if (action.type === 'DISPLAY_UTILITY_POPUP') {
        return action.payload;
    }
    return data;
}

/**
 * hotspot mode reducer
 *
 * @param mode
 * @param action
 * @returns {string|*}
 */
export const hotspotModeReducer = (mode = 'tooltip', action) => {
    if (action.type === 'SET_HOTSPOT_MODE') {
        return action.payload;
    }
    return mode;
}

/**
 * maps overlay reducer on scroll
 *
 * @param status
 * @param action
 * @returns {boolean|*}
 */
export const mapsOverlayReducer = (status = false, action) => {
    if (action.type === 'DISPLAY_MAPS_OVERLAY') {
        return action.payload;
    }
    return false;
}

/**
 * user relate reducer
 *
 * @param user
 * @param action
 * @returns {null|*}
 */
export const userDetailReducer = (user = null, action) => {
    if (action.type === 'SET_USER_DATA') {
        return action.payload;
    }
    return user;
}

/**
 * coordinate search layer reducer
 *
 * @param defaultData
 * @param action
 * @returns {{status: boolean}|{coordinate, status: boolean}}
 */
export const coordinateSearchLayerReducer = (defaultData = {status: false}, action) => {
    if (action.type === 'ADD_COORDINATE_SEARCH_LAYER') {
        return {
            status    : true,
            coordinate: action.payload,
        }
    } else if (action.type === 'REMOVE_COORDINATE_SEARCH_LAYER') {
        return {
            status: false
        }
    }
    return defaultData;
}

export default combineReducers({
    layers                  : layersLoadReducer,
    activeLayers            : activeLayersReducer,
    layerInfoPopupData      : layerInfoPopupDataReducer,
    restoreLayerPopupData   : restoreLayerPopupDataReducer,
    geoserverAlertPopupData : geoserverAlertPopupDataReducer,
    mapProperties           : mapPropertiesReducer,
    baseLayer               : baseLayerReducer,
    mapExtent               : mapExtentReducer,
    legendsMinified         : legendsMinifiedReducer,
    detailPanelData         : detailPanelDataReducer,
    opacitySliderData       : displayOpacitySliderReducer,
    onboardingPopupData     : onboardingPopupDataReducer,
    mapTooltipData          : mapTooltipDataReducer,
    sipalagaChartData       : sipalagaChartDataReducer,
    chartZoomPopupData      : chartZoomPopupDataReducer,
    textZoomPopupData       : chartTextPopupDataReducer,
    mitraRestorasiFilterData: mitraRestorasiFilterDataReducer,
    hotspotFilterData       : hotspotFilterDataReducer,
    multistackData          : multistackDataReducer,
    drawMode                : drawModeReducer,
    mobileMenu              : mobileMenuReducer,
    adhocLayerPanel         : adhocLayerPanelReducer,
    utilityPopup            : utilityPopupReducer,
    hotspotMode             : hotspotModeReducer,
    mapsOverlay             : mapsOverlayReducer,
    user                    : userDetailReducer,
    coordinateSearchLayer   : coordinateSearchLayerReducer,
});
